import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, newRecordButtonStyle,
  toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import {
  useC360List, C360Table, ColumnChooser,
  ViewIcon, EditIcon, DeleteIcon,
  buildSelectFields,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';

const API_BASE = '/api/Licenses';

const ACTIVE_OPTIONS = [{ value: 'true', label: 'Aktif' }, { value: 'false', label: 'Pasif' }];

const COLUMN_FILTER_META = {
  name: { type: 'text', odataField: 'Name' },
  edition: { type: 'text', odataField: 'Edition' },
  isActive: { type: 'select', odataField: 'IsActive', valueFormat: 'raw', options: ACTIVE_OPTIONS },
};
const SEARCH_FIELDS = ['Name', 'Edition'];

export function LicensesPage() {
  usePageMeta('Lisanslar', 'Lisans planlarını tanımlayın ve yönetin');
  const navigate = useNavigate();
  const { canAdd, canEdit, canDelete } = usePageActions();

  const [selected, setSelected] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };

  // $select: görünür kolonların OData alanları + 'Id'. MaxUsers/DurationDays kolonları
  // COLUMN_FILTER_META'da olmadığından (filtresiz accessor) `always`'e eklenir, yoksa boş gelir.
  const selectFields = useMemo(
    () => buildSelectFields({ columnFilterMeta: COLUMN_FILTER_META, columnVisibility, always: ['Id', 'MaxUsers', 'DurationDays'] }),
    [columnVisibility],
  );

  const list = useC360List({
    apiBase: API_BASE,
    columnFilterMeta: COLUMN_FILTER_META,
    searchFields: SEARCH_FIELDS,
    defaultSortField: 'Name',
    selectFields,
    onError: (msg) => showToast('error', msg),
  });

  const {
    rows, totalCount, loading,
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    fetchData,
  } = list;

  const openNew = () => navigate('/licenses/new');
  const openDetail = (row) => { if (!row) return; navigate(`/licenses/${row.id}`, { state: { row } }); };
  const openEditRow = (row) => { if (!row) return; navigate(`/licenses/${row.id}?edit=1`, { state: { row } }); };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API_BASE}/Delete`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id: deleteConfirm.id }) });
      const body = await res.json().catch(() => null);
      if (!res.ok || body?.success === false) throw new Error(body?.message || 'Kayıt silinemedi');
      showToast('success', 'Lisans silindi');
      setDeleteConfirm(null);
      setSelected(null);
      fetchData();
    } catch (error) {
      showToast('error', error.message || 'Silme işlemi sırasında hata oluştu');
      setDeleteConfirm(null);
    } finally {
      setDeleting(false);
    }
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('name', { header: 'Plan Adı', meta: { filterType: 'text' }, cell: (info) => info.getValue() || '-' }),
    columnHelper.accessor('edition', {
      header: 'Edisyon', meta: { filterType: 'text' },
      cell: (info) => { const v = info.getValue(); return v ? <span className="c360-erp-chip">{v}</span> : '-'; },
    }),
    columnHelper.accessor('maxUsers', { header: 'Maks. Kullanıcı', cell: (info) => info.getValue() ?? '-' }),
    columnHelper.accessor('durationDays', { header: 'Süre (gün)', cell: (info) => info.getValue() ?? 'Süresiz' }),
    columnHelper.accessor('isActive', {
      header: 'Durum', meta: { filterType: 'select' },
      cell: (info) => (info.getValue() ? <StatusBadge tone="ok">Aktif</StatusBadge> : <StatusBadge tone="neutral">Pasif</StatusBadge>),
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Görüntüle" onClick={(e) => { e.stopPropagation(); openDetail(row.original); }}><ViewIcon /></button>
          {canEdit && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title="Değiştir" onClick={(e) => { e.stopPropagation(); openEditRow(row.original); }}><EditIcon /></button>}
          {canDelete && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--delete" title="Sil" onClick={(e) => { e.stopPropagation(); setSelected(row.original); setDeleteConfirm(row.original); }}><DeleteIcon /></button>}
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canEdit, canDelete]);

  const sorting = useMemo(() => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []), [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('Name', 'asc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Plan adı veya edisyon ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={rows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Lisanslar"
        subtitle="Lisans planlarını tanımlayın; tenant'lara bu planları atayın."
        actions={canAdd && <button style={newRecordButtonStyle} onClick={openNew}>+ Yeni Lisans</button>}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <C360Table
          table={table}
          columnFilterMeta={COLUMN_FILTER_META}
          columnFilters={columnFilters}
          onApplyFilter={setColumnFilter}
          onClearFilter={clearColumnFilter}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={openDetail}
          emptyText="Kayıt bulunamadı"
        />
      </ListPageShell>

      {deleteConfirm && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(null)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Silme Onayı</h3></div>
            <div style={s.modalBody}><p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}><strong>"{deleteConfirm?.name}"</strong> lisansını silmek istediğinize emin misiniz? Bir tenant'a atanmışsa silinemez.</p></div>
            <div style={s.modalFooter}>
              <button style={{ ...s.btnDanger, opacity: deleting ? 0.6 : 1 }} onClick={handleDelete} disabled={deleting}>{deleting ? 'Siliniyor...' : 'Evet, Sil'}</button>
              <button style={s.btnSecondary} onClick={() => setDeleteConfirm(null)} disabled={deleting}>İptal</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}
